import {useMemo, useState} from 'react';
import {SVG} from '../../../../../constants/assetLists';
import {useAnnouncementTaskMutations} from '../../../../../hooks/useAnnouncementTaskMutations';
import AnnouncementView from '../../Modals/AnnouncementView';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import ReservationAnnouncementLabel from '../../AnnouncementsAndTasks/components/ReservationAnnouncementLabel';
import { getAnnouncementIcon, isReservationAnnouncement } from '../../../../../utils';

type Props = {
  announcement: any;
  index: number;
  groupId?: string;
};

function BriefBox({announcement, index, groupId}: Props) {
  const {setSelectedAnnouncementId, dailyBriefGroupBy} = useOperationsContext();
  const [showAnnouncementViewModal, setShowAnnouncementViewModal] =
    useState(false);

  const isAlert = announcement?.announcementType == 'Error';
  const isUrgent = announcement.isUrgent;

  const startDateUtc = announcement?.scheduling?.startDate; // ISO UTC string
  const msPerDay = 24 * 60 * 60 * 1000;
  // Normalize both dates to UTC midnight to avoid timezone drift
  const utcStartOfDay = (d: Date) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  const dueDate = startDateUtc ? new Date(startDateUtc) : null;
  const now = new Date();

  const daysLeft =
    dueDate == null
      ? null
      : Math.ceil((utcStartOfDay(dueDate) - utcStartOfDay(now)) / msPerDay);

  // YELLOW → 4 weeks to 1 week before startDate (28..8 days left)
  const isRegulationDue =
    announcement?.announcementType === 'Regulation' &&
    daysLeft != null &&
    daysLeft <= 28 &&
    daysLeft > 7;

  // RED → 1 week before OR on/after due date (<= 7 days left, including overdue)
  const isRegulationOverDue =
    announcement?.announcementType === 'Regulation' &&
    daysLeft != null &&
    daysLeft <= 7;

  const hasTasks = announcement?.tasks && announcement?.tasks.length > 0;
  const isAnnouncementDone = announcement?.isCompleted;
  const isAllTasksDone = hasTasks
    ? announcement?.tasks?.every((task: any) => task?.isCompleted) &&
      isAnnouncementDone
    : isAnnouncementDone;

  const {toggleAnnouncementResponderMutation} = useAnnouncementTaskMutations();

  const handleAnnouncementPress = (announcement: any) => {
    setSelectedAnnouncementId(announcement._id);
    setShowAnnouncementViewModal(true);
  };

  const handleToggleResponder = () => {
    toggleAnnouncementResponderMutation.mutate(announcement._id);
  };

  const storedUser = localStorage.getItem('user');
  const currentUserId = useMemo(() => {
    return storedUser ? JSON.parse(storedUser)?._id : null;
  }, [storedUser]);

  const hasResponded = useMemo(() => {
    if (!currentUserId || !announcement?.respondedUsers) return false;
    return announcement.respondedUsers.some(
      (r: any) => r?.user?._id === currentUserId,
    );
  }, [announcement?.respondedUsers, currentUserId]);

  const isReserved = isReservationAnnouncement(announcement);
  const showAnnIcon = dailyBriefGroupBy === 'announcementType'
  const annIcon = getAnnouncementIcon(announcement?.announcementType, announcement?.isUrgent, groupId);

  return (
    <>
      <div
        className="group px-2 py-1 rounded-[2px]  hover:!bg-lightIcyBlue transition-colors duration-200"
        style={{
          background: isReserved
            ? announcement?.reservation_zone?.color
            : "white",
        }}
        key={index}
        onClick={(e) => {
          e.stopPropagation();
          handleAnnouncementPress(announcement);
        }}
      >
        <div className=" flex items-center justify-between ">
          {/* Left side: checkbox and content */}
          <div className="flex-1 flex items-center gap-2">
            <div
              className={`flex-1 flex items-center gap-2 rounded-[8px] px-2 py-1 ${
                isRegulationDue
                  ? "bg-brightYellow"
                  : isRegulationOverDue
                    ? "bg-lightRed"
                    : ""
              }`}
            >

              {showAnnIcon &&(isAlert || isRegulationDue || isRegulationOverDue) && (
                <div className="flex items-center">
                  {!isAlert && (
                    <div
                      className={`w-[10px] h-[10px] rounded-full ${
                        isRegulationDue
                          ? "bg-goldenYellow"
                          : isRegulationOverDue
                            ? "bg-[#C2C2C2]"
                            : ""
                      }`}
                    />
                  )}
                  <div className="w-[14px] h-[13px]">
                    <img
                      src={
                        isAlert || isRegulationOverDue
                          ? SVG.redTriangleIcon
                          : SVG.grayTriangleIcon
                      }
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
              {showAnnIcon && !isAlert && (
                  <div className="w-[20px] h-[16px] shrink-0">
                    <img
                      src={annIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              {isReserved ? (
                <ReservationAnnouncementLabel
                  announcement={announcement}
                  isAllTasksDone={isAllTasksDone}
                  labelClasses="!font-normal"
                />
              ) : (
                <span
                  className={`text-lg min-w-0 break-all whitespace-normal
              ${
                isRegulationDue && isUrgent
                  ? " text-[#ff95b2] "
                  : isAlert || isUrgent
                    ? "text-raspberryRed"
                    : "text-black"
              }
              ${isAllTasksDone && "line-through"}`}
                >
                  {announcement?.announcementTitle || ""}
                </span>
              )}
            </div>
          </div>
          {
            <button
              className=" w-[24px] h-[24px]"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleResponder();
              }}
            >
              <img
                src={
                  hasResponded ? SVG.thumbsUpGreenIcon : SVG.thumbsUpGrayIcon
                }
                alt="like"
                className="w-full h-full object-contain"
              />
            </button>
          }
        </div>
        {isReserved && (
          <p className="text-base px-2 max-w-sm truncate">
            {announcement?.description}
          </p>
        )}
      </div>
      <AnnouncementView
        open={showAnnouncementViewModal}
        onClose={() => {
          setShowAnnouncementViewModal(false);
          setSelectedAnnouncementId(null);
        }}
      />
    </>
  );
}

export default BriefBox;
